import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useTradingRulesConfig } from '../../contexts/TradingRulesConfigContext';
import { AuthContext } from '../../contexts/AuthContext';
import { TradingContext } from '../../contexts/TradingContext';
import analyticsService from '../../services/analyticsService';
import { SIGNUP_FUNNEL_EVENTS } from '../../utils/onboardingAnalytics';
import { isTrackTier } from '../../utils/authAccess';
import { readConnectTradeCallbackParams } from '../../utils/connectTradeCallbackErrors';
import {
  getOrientationConnectPending,
  getOrientationSetupProgress,
  needsBrokerConnection,
  setOrientationConnectPending,
  shouldShowTradeRiskOrientation,
} from '../../utils/onboardingStatus';
import { useUpdateAppSettings } from '../../hooks/useBackend';
import OrientationWelcomeStep from './steps/OrientationWelcomeStep';
import OrientationRulesStep from './steps/OrientationRulesStep';
import OrientationReadyStep from './steps/OrientationReadyStep';
import OrientationConnectStep from './steps/OrientationConnectStep';

/**
 * Baseline rules for the enable-rules step when no account is selected yet.
 * Prefer an existing user template, else seed max daily loss from Activate Protection.
 */
function buildRulesDefaults(appSettings) {
  const template = appSettings?.defaultTradingRulesTemplate;
  if (template?.rule_thresholds || template?.enforcement_config) {
    return {
      rule_thresholds: { ...(template.rule_thresholds || {}) },
      enforcement_config: { ...(template.enforcement_config || {}) },
      additional_rules: { ...(template.additional_rules || {}) },
    };
  }

  const daily = appSettings?.pendingDailyLossRule;
  if (daily && Number(daily.value) > 0 && daily.mode !== 'percent') {
    const dollar = Math.abs(Number(daily.value));
    if (dollar) {
      return {
        rule_thresholds: { max_loss_per_day: -dollar },
        enforcement_config: {
          max_loss_per_day: {
            enabled: true,
            enforce: daily.enforcement === 'enforce',
            alert: daily.enforcement !== 'enforce',
          },
        },
        additional_rules: {},
      };
    }
  }

  return {
    rule_thresholds: {},
    enforcement_config: {},
    additional_rules: {},
  };
}

function normalizeTemplate(settings) {
  return {
    rule_thresholds: { ...(settings?.rule_thresholds || {}) },
    enforcement_config: { ...(settings?.enforcement_config || {}) },
    additional_rules: { ...(settings?.additional_rules || {}) },
    updatedAt: new Date().toISOString(),
  };
}

const PHASE = {
  INTRO: 'intro',
  RULES: 'rules',
  CONNECT: 'connect',
  READY: 'ready',
};

function isReturningFromBrokerConnect(user, search) {
  if (readConnectTradeCallbackParams(search).ctSuccess) return true;
  return getOrientationConnectPending(user?.id);
}

/**
 * Post-onboarding orientation — one modal shell; steps swap content in place
 * (intro → rules → connect | ready) so the frame does not flicker open/close.
 */
export default function TradeRiskSetupModal() {
  const { colors } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshUserSettings } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const { settings, saveSettings, loading } = useTradingRulesConfig();
  const { updateAppSettings } = useUpdateAppSettings();
  const rulesFormRef = useRef(null);
  const [phase, setPhase] = useState(PHASE.INTRO);
  const phaseRef = useRef(PHASE.INTRO);
  phaseRef.current = phase;
  const markingSeenRef = useRef(false);
  const brokerLinkedTrackedRef = useRef(false);
  const [dismissedLocally, setDismissedLocally] = useState(false);
  const [connectBusy, setConnectBusy] = useState(false);
  const [localSettings, setLocalSettings] = useState(() => buildRulesDefaults(user?.appSettings));

  const fromQuery = useMemo(
    () => new URLSearchParams(location.search).get('setup_risk') === '1',
    [location.search]
  );
  const returningFromBroker = isReturningFromBrokerConnect(user, location.search);
  const ctSuccess = readConnectTradeCallbackParams(location.search).ctSuccess;
  const brokerLinked = !needsBrokerConnection(user) || !!ctSuccess;
  const autoShow = shouldShowTradeRiskOrientation(user);
  const show = !dismissedLocally && (fromQuery || autoShow);
  const accountId = selectedAccount?.AccountID;
  const hasAccount = !!accountId;
  const progress = useMemo(
    () => getOrientationSetupProgress(user, settings, { brokerConnected: brokerLinked }),
    [user, settings, brokerLinked]
  );
  const essentials = isTrackTier(user);
  const completeAfterBrokerReturn = returningFromBroker && brokerLinked;

  useEffect(() => {
    setDismissedLocally(false);
  }, [user?.id]);

  useEffect(() => {
    if (hasAccount) return;
    setLocalSettings(buildRulesDefaults(user?.appSettings));
  }, [
    hasAccount,
    user?.appSettings?.defaultTradingRulesTemplate,
    user?.appSettings?.pendingDailyLossRule,
  ]);

  const markOrientationSeen = useCallback(async () => {
    if (markingSeenRef.current) return;
    if (user?.appSettings?.tradeRiskOrientationSeen === true) return;
    if (!user?.token) return;
    markingSeenRef.current = true;
    try {
      await updateAppSettings(user.token, {
        tradeRiskOrientationSeen: true,
      });
      if (refreshUserSettings) await refreshUserSettings();
    } catch (err) {
      console.warn('[TradeRiskSetupModal] Failed to persist orientation seen:', err);
      markingSeenRef.current = false;
    }
  }, [user?.token, user?.appSettings, updateAppSettings, refreshUserSettings]);

  const dismiss = useCallback(() => {
    setDismissedLocally(true);
    setConnectBusy(false);
    setOrientationConnectPending(user?.id, false);
    markOrientationSeen();
    const p = new URLSearchParams(location.search);
    p.delete('setup_risk');
    const s = p.toString();
    navigate(`${location.pathname}${s ? `?${s}` : ''}`, { replace: true });
  }, [navigate, location.pathname, location.search, markOrientationSeen, user?.id]);

  useEffect(() => {
    if (!show) return;
    setConnectBusy(false);

    if (completeAfterBrokerReturn) {
      if (essentials) setPhase(PHASE.READY);
      return;
    }

    if (returningFromBroker) {
      setPhase(PHASE.CONNECT);
      return;
    }

    if (progress.setupComplete) {
      if (essentials) setPhase(PHASE.READY);
      return;
    }

    setPhase(PHASE.INTRO);
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps -- fire once per open

  // Finish orientation after broker OAuth (full-page redirect or another tab).
  useEffect(() => {
    if (!show) {
      brokerLinkedTrackedRef.current = false;
      return;
    }
    if (!completeAfterBrokerReturn && !progress.setupComplete) {
      brokerLinkedTrackedRef.current = false;
      return;
    }
    if (brokerLinkedTrackedRef.current) return;
    brokerLinkedTrackedRef.current = true;
    setOrientationConnectPending(user?.id, false);
    if (essentials) {
      setPhase(PHASE.READY);
      return;
    }
    dismiss();
  }, [show, completeAfterBrokerReturn, progress.setupComplete, essentials, user?.id, dismiss]);

  const handleContinueFromIntro = useCallback(() => {
    if (progress.rulesConfigured && !essentials && needsBrokerConnection(user)) {
      setOrientationConnectPending(user?.id, true);
      setPhase(PHASE.CONNECT);
      return;
    }
    setPhase(PHASE.RULES);
  }, [user, essentials, progress.rulesConfigured]);

  const persistDefaultTemplate = useCallback(
    async (newSettings, { applyOnce } = {}) => {
      if (!user?.token) return false;
      const template = normalizeTemplate(newSettings);
      setLocalSettings(template);
      await updateAppSettings(user.token, {
        defaultTradingRulesTemplate: template,
        applyDefaultTradingRulesTemplateOnce: applyOnce === true,
        pendingDailyLossRule: null,
      });
      return true;
    },
    [user?.token, user?.appSettings, updateAppSettings]
  );

  const saveRules = useCallback(
    async (newSettings) => {
      if (hasAccount) {
        await persistDefaultTemplate(newSettings, { applyOnce: false });
        return saveSettings(newSettings);
      }
      await persistDefaultTemplate(newSettings, { applyOnce: true });
      return true;
    },
    [persistDefaultTemplate, hasAccount, saveSettings]
  );

  const effectiveSettings = hasAccount ? settings : localSettings;
  const rulesReady = hasAccount ? (!loading && !!settings) : true;

  const handleRulesContinue = useCallback(async () => {
    try {
      await rulesFormRef.current?.quickSetupFlushPendingEnforce?.();
    } catch {
      return;
    }
    analyticsService.trackTradingEvent(SIGNUP_FUNNEL_EVENTS.TRADING_RULES_SET, {
      user_id: user?.id,
      source: 'orientation',
    });

    const nextEssentials = isTrackTier(user);
    if (!nextEssentials && needsBrokerConnection(user)) {
      setOrientationConnectPending(user?.id, true);
      setPhase(PHASE.CONNECT);
      return;
    }

    if (!nextEssentials) {
      dismiss();
      return;
    }

    setPhase(PHASE.READY);
  }, [user, dismiss]);

  const handleStartTrading = useCallback(() => {
    dismiss();
  }, [dismiss]);

  const handleSkipIntro = useCallback(() => {
    dismiss();
  }, [dismiss]);

  const handleModalHide = useCallback(() => {
    if (connectBusy) return;
    const current = phaseRef.current;
    if (current === PHASE.INTRO) {
      handleSkipIntro();
      return;
    }
    // Rules / connect / ready: closing finishes orientation (rules already auto-save).
    handleStartTrading();
  }, [connectBusy, handleSkipIntro, handleStartTrading]);

  const suppressIncompleteIntro =
    show &&
    !essentials &&
    (completeAfterBrokerReturn || progress.setupComplete);

  if (!show || suppressIncompleteIntro) {
    return null;
  }

  const isIntro = phase === PHASE.INTRO;
  const isRules = phase === PHASE.RULES;
  const isConnect = phase === PHASE.CONNECT;
  const isReady = phase === PHASE.READY;
  const useQuietHeader = isIntro || isRules || isConnect;
  const shellClass = [
    'trade-risk-quick-setup-modal',
    'onboarding-flow-modal',
    useQuietHeader ? 'trade-risk-quick-setup-modal--intro' : '',
    isRules ? 'trade-risk-quick-setup-modal--rules' : '',
    isConnect ? 'trade-risk-quick-setup-modal--connect' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const dialogClass = [
    'trade-risk-quick-setup-dialog',
    'onboarding-flow-dialog',
    isConnect ? 'connect-broker-modal connect-broker-modal--fixed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  let body = null;
  if (isIntro) {
    body = (
      <OrientationWelcomeStep
        rulesConfigured={progress.rulesConfigured}
        brokerConnected={progress.brokerConnected}
      />
    );
  } else if (isReady) {
    body = <OrientationReadyStep />;
  } else if (isConnect) {
    body = <OrientationConnectStep onBusyChange={setConnectBusy} />;
  } else {
    body = (
      <OrientationRulesStep
        ref={rulesFormRef}
        accountId={accountId || null}
        settings={effectiveSettings}
        saveSettings={saveRules}
        ready={rulesReady}
      />
    );
  }

  let footer = null;
  if (isIntro) {
    footer = progress.setupComplete ? (
      <Button
        variant="primary"
        onClick={handleStartTrading}
        className="text-nowrap trade-risk-intro__cta"
        style={{ width: 'auto', flex: '0 0 auto' }}
      >
        Start trading
      </Button>
    ) : (
      <>
        <Button
          variant="link"
          onClick={handleSkipIntro}
          className="text-nowrap"
          style={{
            color: colors.textMuted || colors.textLight,
            textDecoration: 'none',
            fontStyle: 'normal',
            fontSize: '0.875rem',
            width: 'auto',
            flex: '0 0 auto',
            paddingLeft: 0,
          }}
        >
          Skip setup
        </Button>
        <Button
          variant="primary"
          onClick={handleContinueFromIntro}
          className="text-nowrap trade-risk-intro__cta"
          style={{ width: 'auto', flex: '0 0 auto' }}
        >
          {progress.rulesConfigured && !progress.brokerConnected
            ? 'Connect Broker'
            : 'Set up trading rules'}
        </Button>
      </>
    );
  } else if (isRules) {
    footer = (
      <Button
        variant="primary"
        onClick={handleRulesContinue}
        className="text-nowrap"
        style={{ width: 'auto', flex: '0 0 auto' }}
      >
        {needsBrokerConnection(user) ? 'Connect Broker' : 'Done'}
      </Button>
    );
  } else if (isReady) {
    footer = (
      <Button
        variant="primary"
        onClick={handleStartTrading}
        className="text-nowrap"
        style={{ width: 'auto', flex: '0 0 auto' }}
      >
        Go to Dashboard
      </Button>
    );
  }

  return (
    <Modal
      show
      onHide={handleModalHide}
      centered
      backdrop={connectBusy ? 'static' : true}
      keyboard={!connectBusy}
      backdropClassName="trade-risk-quick-setup-backdrop"
      className={shellClass}
      dialogClassName={dialogClass}
      contentClassName="trade-risk-quick-setup-content"
      style={{ zIndex: 10050 }}
    >
      <Modal.Header
        closeButton={!connectBusy}
        className={useQuietHeader ? 'trade-risk-intro-header' : undefined}
      >
        {isReady && (
          <Modal.Title
            as="h2"
            style={{
              fontSize: '1.05rem',
              fontWeight: 700,
              color: colors.text,
            }}
          >
            You&apos;re all set
          </Modal.Title>
        )}
      </Modal.Header>

      <Modal.Body
        className={`onboarding-flow-modal-body${isConnect ? ' connect-broker-modal-body' : ''}`}
        style={{
          overflowY: isRules ? 'auto' : 'hidden',
        }}
      >
        {body}
      </Modal.Body>

      {footer && (
        <Modal.Footer
          className="trade-risk-quick-setup-footer"
          style={{
            justifyContent:
              isRules || isReady || (isIntro && progress.setupComplete) ? 'flex-end' : 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          {footer}
        </Modal.Footer>
      )}
    </Modal>
  );
}
